
import { FastingPersistentState } from './types';

/**
 * Loads fasting state from localStorage
 * @returns The parsed fasting state or null if not found
 */
export const loadFastingStateFromStorage = (): FastingPersistentState | null => {
  try {
    const savedState = localStorage.getItem('fastingState');
    if (!savedState) {
      console.log("[Storage] No saved state found in localStorage");
      return null;
    }
    
    const parsedState = JSON.parse(savedState) as FastingPersistentState;
    
    // Basic validation - only reject if the object is completely invalid
    if (!parsedState || typeof parsedState !== 'object') {
      console.warn("[Storage] Invalid state format in localStorage");
      localStorage.removeItem('fastingState');
      return null;
    }
    
    // Validate timeElapsed is not negative
    if (parsedState.timeElapsed && parsedState.timeElapsed < 0) {
      parsedState.timeElapsed = 0;
    }
    
    // Ensure we have basic required properties with defaults
    const validatedState: FastingPersistentState = {
      isActive: parsedState.isActive || false,
      isPaused: parsedState.isPaused || false,
      timeElapsed: parsedState.timeElapsed || 0,
      selectedProtocolId: parsedState.selectedProtocolId,
      selectedProtocol: parsedState.selectedProtocol,
      waterIntake: parsedState.waterIntake || 0,
      startTime: parsedState.startTime || null,
      pausedTime: parsedState.pausedTime || null,
      endTime: parsedState.endTime || null,
      totalPauseDuration: parsedState.totalPauseDuration || 0
    };
    
    console.log("[Storage] Successfully loaded state from localStorage:", {
      isActive: validatedState.isActive,
      protocol: validatedState.selectedProtocol?.name || validatedState.selectedProtocolId,
      startTime: validatedState.startTime ? new Date(validatedState.startTime).toLocaleString() : 'none',
      timeElapsed: validatedState.timeElapsed,
      hasStartTime: !!validatedState.startTime
    });
    
    return validatedState;
  } catch (error) {
    console.error("[Storage] Error parsing saved fasting state:", error);
    // Remove invalid data
    localStorage.removeItem('fastingState');
    return null;
  }
};

/**
 * Saves state to localStorage
 */
export const saveToLocalStorage = (state: FastingPersistentState): void => {
  try {
    console.log("[Storage] Saving state to localStorage:", {
      isActive: state.isActive,
      protocol: state.selectedProtocol?.name,
      startTime: state.startTime ? new Date(state.startTime).toLocaleString() : 'none',
      timeElapsed: state.timeElapsed
    });
    
    localStorage.setItem('fastingState', JSON.stringify(state));
    localStorage.setItem('fastingStateLastSaved', Date.now().toString());
  } catch (error) {
    console.error("[Storage] Error saving to localStorage:", error);
  }
};

/**
 * Clears localStorage data
 */
export const clearLocalStorage = (): void => {
  console.log("[Storage] Clearing localStorage data");
  localStorage.removeItem('fastingState');
  localStorage.removeItem('fastingStateLastSaved');
};
